"use client";

// NavBarWidget — Horizontal/vertical navigation with mega menu support.
// Desktop: horizontal NavigationMenu with mega panel dropdowns.
// Mobile: hamburger button + Sheet drawer with expandable mega panels.

import { CaretDown } from "@phosphor-icons/react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type React from "react";
import { useEffect, useState } from "react";
import { MenuJustifiedIcon } from "@/components/storefront/icons/MenuJustifiedIcon";
import { IconButton } from "@/components/storefront/ui/icon-button";
import type {
  NavBarConfig,
  NavBarLink,
} from "@/lib/structured-builder/widget-types/nav-bar";
import { usePageIdle } from "@/hooks/use-page-idle";
import { isLinkActive } from "./is-link-active";

const NavBarMobileDrawer = dynamic(
  () => import("./NavBarMobileDrawer").then((m) => m.NavBarMobileDrawer),
  { ssr: false },
);

const ACTIVE_STYLE_CLASS: Record<NavBarConfig["active_style"], string> = {
  underline: "underline underline-offset-4 decoration-2",
  highlight:
    "bg-[color:var(--primary)]/10 text-[color:var(--primary)] rounded-full px-3 py-1",
  bold: "font-bold",
};

type Props = {
  config: NavBarConfig;
  locale: string;
  /** Pre-rendered mega panel JSX, keyed by panel node ID (passed from server TreeRenderer) */
  renderedPanels?: Record<string, React.ReactNode>;
  /** Where this nav bar is rendered. In the footer, mobile shows inline links
   *  instead of a hamburger + drawer (a drawer is a header-only affordance). */
  surface?: "page" | "header" | "footer";
};

export function NavBarWidget({
  config,
  locale,
  renderedPanels,
  surface = "page",
}: Props) {
  const isRTL = locale === "ar";
  const links = config.links ?? [];
  const isVertical = config.style === "vertical";
  const pathname = usePathname() ?? "/";
  const activeClass = ACTIVE_STYLE_CLASS[config.active_style] ?? "";

  const gapClass =
    config.gap === "sm" ? "gap-2" : config.gap === "lg" ? "gap-8" : "gap-5";
  const fontClass =
    config.font_size === "sm"
      ? "text-xs"
      : config.font_size === "lg"
        ? "text-base"
        : "text-sm";
  // tap-target: a bare text link is ~20px tall at text-sm, under the WCAG 2.2
  // AA 24px floor (SC 2.5.8). The overlay lifts the hit area without changing
  // the type size or the bar's height.
  const linkClass = `tap-target ${fontClass} font-medium text-foreground/80 hover:text-foreground transition-colors`;

  // Vertical style — no mobile handling needed (used in footer / mega panels)
  if (isVertical) {
    return (
      <nav className={`flex flex-col ${gapClass}`}>
        {links.map((link: NavBarLink) => {
          const active = isLinkActive(pathname, link.url);
          return (
            <Link
              key={link.id}
              href={link.url}
              className={`${linkClass}${active ? ` ${activeClass}` : ""}`}
            >
              {isRTL ? link.label_ar : link.label_en}
            </Link>
          );
        })}
      </nav>
    );
  }

  const hasMegaPanels =
    renderedPanels &&
    links.some((l) => l.panel_node_id && renderedPanels[l.panel_node_id]);

  return (
    <>
      {/* Desktop: horizontal NavigationMenu — hidden on mobile */}
      <nav className="hidden md:block">
        <DesktopNav
          links={links}
          renderedPanels={renderedPanels}
          isRTL={isRTL}
          gapClass={gapClass}
          linkClass={linkClass}
          pathname={pathname}
          activeClass={activeClass}
        />
      </nav>

      {/* Mobile: in the footer, show links inline (no hamburger — a drawer is a
          header affordance). Elsewhere, hamburger + drawer. */}
      {surface === "footer" ? (
        <FooterInlineNav
          links={links}
          isRTL={isRTL}
          linkClass={linkClass}
          gapClass={gapClass}
          pathname={pathname}
          activeClass={activeClass}
        />
      ) : (
        <MobileNav
          links={links}
          renderedPanels={hasMegaPanels ? renderedPanels : undefined}
          isRTL={isRTL}
          pathname={pathname}
          activeClass={activeClass}
        />
      )}
    </>
  );
}

// ============================================================
// FOOTER INLINE NAV — mobile-only wrapped links (no drawer)
// ============================================================

function FooterInlineNav({
  links,
  isRTL,
  linkClass,
  gapClass,
  pathname,
  activeClass,
}: {
  links: NavBarLink[];
  isRTL: boolean;
  linkClass: string;
  gapClass: string;
  pathname: string;
  activeClass: string;
}) {
  return (
    <nav
      className={`md:hidden flex flex-wrap items-center justify-center gap-y-2 ${gapClass}`}
    >
      {links.map((link) => {
        const active = isLinkActive(pathname, link.url);
        return (
          <Link
            key={link.id}
            href={link.url}
            className={`${linkClass}${active ? ` ${activeClass}` : ""}`}
            {...(link.open_new_tab
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
          >
            {isRTL ? link.label_ar : link.label_en}
          </Link>
        );
      })}
    </nav>
  );
}

// ============================================================
// DESKTOP NAV — horizontal with mega menu dropdowns
// ============================================================

type DesktopNavProps = {
  links: NavBarLink[];
  renderedPanels?: Record<string, React.ReactNode>;
  isRTL: boolean;
  gapClass: string;
  linkClass: string;
  pathname: string;
  activeClass: string;
};

type DesktopMenu = typeof import("./NavBarDesktopMenu").NavBarDesktopMenu;

// The interactive menu (base-ui NavigationMenu + floating-ui) loads on desktop
// once the page is idle, or at once if the visitor points at or focuses the
// menu first. Until then — and always on phones, where this nav is hidden —
// StaticDesktopNav renders the same closed-state markup, so the links are in
// the HTML and the swap doesn't move anything.
function DesktopNav(props: DesktopNavProps) {
  const [Menu, setMenu] = useState<DesktopMenu | null>(null);
  const [wanted, setWanted] = useState(false);
  const [openId, setOpenId] = useState<string>();
  const pageIdle = usePageIdle();

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 768px)").matches;
    if (!wanted && !(pageIdle && desktop)) return;
    let live = true;
    import("./NavBarDesktopMenu").then((m) => {
      if (live) setMenu(() => m.NavBarDesktopMenu);
    });
    return () => {
      live = false;
    };
  }, [wanted, pageIdle]);

  if (Menu) return <Menu {...props} defaultOpenId={openId} />;
  return (
    <StaticDesktopNav
      {...props}
      onIntent={() => setWanted(true)}
      onOpen={(id) => {
        setOpenId(id);
        setWanted(true);
      }}
    />
  );
}

function StaticDesktopNav({
  links,
  renderedPanels,
  isRTL,
  gapClass,
  linkClass,
  pathname,
  activeClass,
  onIntent,
  onOpen,
}: DesktopNavProps & {
  onIntent: () => void;
  onOpen: (id: string) => void;
}) {
  return (
    <nav dir={isRTL ? "rtl" : "ltr"} onPointerEnter={onIntent} onFocus={onIntent}>
      <ul aria-orientation="horizontal" className={`flex items-center ${gapClass}`}>
        {links.map((link) => {
          const label = isRTL ? link.label_ar : link.label_en;
          const hasMega = !!(link.panel_node_id && renderedPanels?.[link.panel_node_id]);
          const active = isLinkActive(pathname, link.url);
          const activeCls = active ? ` ${activeClass}` : "";

          if (!hasMega) {
            return (
              <li key={link.id}>
                {link.open_new_tab ? (
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className={`${linkClass}${activeCls}`}
                  >
                    {label}
                  </a>
                ) : (
                  <Link href={link.url} className={`${linkClass}${activeCls}`}>
                    {label}
                  </Link>
                )}
              </li>
            );
          }

          return (
            <li key={link.id}>
              <button
                type="button"
                aria-expanded={false}
                onClick={() => onOpen(link.id)}
                className={`${linkClass}${activeCls} inline-flex items-center gap-1 cursor-pointer`}
              >
                {label}
                <span aria-hidden="true" className="transition-transform duration-200">
                  <CaretDown weight="bold" className="size-3" />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

// ============================================================
// MOBILE NAV — hamburger button + sheet drawer
// ============================================================

function MobileNav({
  links,
  renderedPanels,
  isRTL,
  pathname,
  activeClass,
}: {
  links: NavBarLink[];
  renderedPanels?: Record<string, React.ReactNode>;
  isRTL: boolean;
  pathname: string;
  activeClass: string;
}) {
  const [open, setOpen] = useState(false);
  // Mounted closed on idle so the first open still animates in.
  const pageIdle = usePageIdle();

  return (
    <>
      <IconButton
        variant="text"
        color="default"
        size="md"
        className="md:hidden -order-1"
        aria-label={isRTL ? "فتح القائمة" : "Open menu"}
        onClick={() => setOpen(true)}
        icon={<MenuJustifiedIcon />}
      />

      {(pageIdle || open) && (
        <NavBarMobileDrawer
          open={open}
          onOpenChange={setOpen}
          links={links}
          renderedPanels={renderedPanels}
          isRTL={isRTL}
          pathname={pathname}
          activeClass={activeClass}
        />
      )}
    </>
  );
}
